// App-level IndexedDB persistence (docs/08-app-design.md Section 7): the
// sealed master, the vault's public facts (its fee reserve's address among
// them) and Settings, plus, on a legacy device only, the sealed per-device
// relayer key of earlier releases. This is distinct from the ledger's own
// IndexedDB store (sdk/ts/src/ledger-idb.ts, database `thevault-ledger`) -
// `forgetDevice` deletes both databases, since "Forget this device" (5.9)
// must leave nothing behind. Nothing here ever touches localStorage: raw
// IndexedDB only, same convention as ledger-idb.ts.
//
// A device's setup (sealed master, vault info) is written by `installSetup`
// in one transaction that first checks the device is empty (R48):
// onboarding can never overwrite an existing setup, whose legacy fee
// reserve key (if any) would be lost with it, and an interrupted write can
// never leave a mixed setup behind. The fee key itself is never stored: the
// worker derives it from the master at every unlock (docs/18-one-fee-reserve.md
// section 3). The one later write to the setup's row is `updateFeeFields`,
// a compare-and-set (R84).

import type { SealedEnvelope, SealedMaster } from '@thevault/sdk';
import type { VaultInfo } from './engine';

const APP_DB = 'thevault-app';
const LEDGER_DB = 'thevault-ledger'; // must match ledger-idb.ts's IndexedDbLedgerStore.open default
/**
 * The fence against later builds (docs/18 section 3, writer step 6; owner
 * decision 4). This build opens `thevault-app` at exactly this version.
 * Opening a database a later build has upgraded fails with a VersionError
 * instead of writing, and a later build's upgrade closes this build's open
 * connection and locks its session (the `onversionchange` handler below).
 * Either way the session says the device was set up by a newer build
 * (session.ts, 'newer-build'), never that it is empty.
 * Version 2 is this release's: a Part A writer stores only the derived
 * n = 0 fee address, so a build that may store another address must bump
 * the version first, and an older build (version 1) opened afterwards fails
 * to open the database. No store changes between 1 and 2.
 */
export const APP_DB_VERSION = 2;
const KV_STORE = 'kv';

const KEY_SEALED_MASTER = 'sealedMaster';
const KEY_SEALED_RELAYER = 'sealedRelayer';
const KEY_VAULT_INFO = 'vaultInfo';
const KEY_SETTINGS = 'settings';

/** How long a delete may stay blocked by another connection before "Forget this device" reports it (R47). The deletion itself keeps waiting and completes once that connection closes. */
const FORGET_BLOCKED_MS = 3_000;

/**
 * Legacy: the per-device relayer seed of earlier releases, sealed at rest
 * under the device passphrase (5.7) by `sealRelayerSeed`
 * (sdk/ts/src/master-storage.ts): the same envelope shape as the master's,
 * but the AAD binds it to the 'relayer' purpose, so a master envelope can
 * never be opened as a relayer envelope or vice versa. This release never
 * writes one and never deletes one; unlock does not read it. It is opened
 * only to move the older reserve's SOL out (docs/18 sections 3 and 6).
 */
export type SealedRelayer = SealedEnvelope;

export interface AppSettings {
  rpcUrl: string;
  jupiterUrl: string;
  /** Stored sealed (never plaintext) once a sealing format exists (5.11); left undefined until then. */
  jupiterApiKeySealed?: SealedMaster;
  autoLockMinutes: number;
  displayCurrency: 'raw' | 'usd';
  excludedVenues: string[];
}

/**
 * The public `api.mainnet-beta.solana.com` endpoint answers browser
 * requests with `403 Access forbidden` (checked 2026-09-26: any request
 * carrying an Origin header), while Node scripts get through, so it can
 * never be the app's default. publicnode's mainnet endpoint is free, needs
 * no key and sets `access-control-allow-origin: *`. Both hosts are in the
 * CSP allowlist (app/.env); Settings (5.11) lets a user pick any allowed one.
 */
export const LEGACY_DEFAULT_RPC = 'https://api.mainnet-beta.solana.com';
/** publicnode's free endpoint, the default until 12 Oct 2026: it now refuses indexed requests (a vault's token accounts,
 * getProgramAccounts for stake discovery) without a personal token, so tokens and stake accounts went unlisted. */
export const PUBLICNODE_RPC = 'https://solana-rpc.publicnode.com';
/** The hosted default: an endpoint set at build time, VITE_DEFAULT_RPC in app/.env.production.local (git-ignored: it
 * carries the owner's API key; its host must be in the CSP allowlist). Without it, publicnode. */
export const DEFAULT_RPC: string = import.meta.env.VITE_DEFAULT_RPC || PUBLICNODE_RPC;

export const DEFAULT_SETTINGS: AppSettings = {
  rpcUrl: DEFAULT_RPC,
  jupiterUrl: 'https://lite-api.jup.ag',
  autoLockMinutes: 10,
  displayCurrency: 'raw',
  excludedVenues: [],
};

/** This device already holds a setup: onboarding refuses to replace it (R48). "Forget this device" is the only way to an empty device. */
export class DeviceOccupied extends Error {
  constructor() {
    super('This device already holds a vault. Forget this device first to set up another.');
    this.name = 'DeviceOccupied';
  }
}

/** Another connection (a second tab of this app) keeps the databases open, so they cannot be deleted yet (R47). */
export class ForgetBlocked extends Error {
  constructor() {
    super('Another tab of this app still holds the vault open, so its data has not been deleted yet. The deletion stays queued and finishes on its own the moment that tab closes; it cannot be cancelled. Close that tab, or try again.');
    this.name = 'ForgetBlocked';
  }
}

function reqPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;
let versionChangeListener: ((upgraded: boolean) => void) | null = null;

/**
 * Called once when another connection asks to delete or upgrade the app database and this one has closed itself
 * (R47); the session locks on it. `upgraded` is true for an upgrade, which only a later build makes (the fence,
 * writer step 6), and false for a deletion (another tab's Forget).
 */
export function onAppDbVersionChange(listener: (upgraded: boolean) => void): void {
  versionChangeListener = listener;
}

/**
 * The version fence's refusal (docs/18 section 3, writer step 6): this build opened a database a later build has
 * upgraded past `APP_DB_VERSION`. `openAppDb` rejects with the browser's DOMException unchanged, so its name is the
 * test; the session reports the device as set up by a newer build rather than as empty.
 */
export function isVersionError(e: unknown): boolean {
  return (e instanceof DOMException || e instanceof Error) && e.name === 'VersionError';
}

function openAppDb(indexedDB: IDBFactory = globalThis.indexedDB): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(APP_DB, APP_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE);
      };
      req.onsuccess = () => {
        const db = req.result;
        // Version-change cooperation: close at once so another tab's deletion is not blocked by this connection.
        db.onversionchange = (event) => {
          db.close();
          dbPromise = null;
          // A deletion has no new version (IndexedDB sets it null); an upgrade has one.
          versionChangeListener?.(event.newVersion !== null);
        };
        resolve(db);
      };
      // The browser's error as it is, never wrapped: a VersionError must keep its name (`isVersionError`).
      req.onerror = () => reject(req.error ?? new Error(`failed to open IndexedDB database ${APP_DB}`));
    });
  }
  return dbPromise;
}

async function kvGet<T>(key: string): Promise<T | undefined> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readonly');
  const value = await reqPromise<T | undefined>(tx.objectStore(KV_STORE).get(key));
  return value === undefined ? undefined : structuredClone(value);
}

async function kvPut<T>(key: string, value: T): Promise<void> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readwrite', { durability: 'strict' });
  tx.objectStore(KV_STORE).put(structuredClone(value), key);
  await txDone(tx);
}

/** A device's setup under this release (docs/18 section 3): no fee key is stored, so no relayer envelope either. `vaultInfo.feeWallet` is the derived reserve's address, display data only. */
export interface DeviceSetup {
  sealedMaster: SealedMaster;
  vaultInfo: VaultInfo;
}

/**
 * Write a device's whole setup in one transaction, refusing with
 * `DeviceOccupied` if a sealed master is already stored (R48). Either both
 * keys are written or neither is; the check and the writes share the
 * transaction, so a second flow racing this one cannot slip in between.
 */
export async function installSetup(setup: DeviceSetup): Promise<void> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readwrite', { durability: 'strict' });
  const store = tx.objectStore(KV_STORE);
  const existing = store.get(KEY_SEALED_MASTER);
  let occupied = false;
  existing.onsuccess = () => {
    if (existing.result !== undefined) {
      occupied = true;
      tx.abort();
      return;
    }
    store.put(structuredClone(setup.sealedMaster), KEY_SEALED_MASTER);
    store.put(structuredClone(setup.vaultInfo), KEY_VAULT_INFO);
  };
  try {
    await txDone(tx);
  } catch (e) {
    if (occupied) throw new DeviceOccupied();
    throw e;
  }
}

export async function loadSealedMaster(): Promise<SealedMaster | undefined> {
  return kvGet<SealedMaster>(KEY_SEALED_MASTER);
}

/** Legacy: the per-device relayer envelope of an earlier release, if this device holds one. Never read at unlock (docs/18 section 3). */
export async function loadSealedRelayer(): Promise<SealedRelayer | undefined> {
  return kvGet<SealedRelayer>(KEY_SEALED_RELAYER);
}

/** Whether this device holds a legacy relayer envelope (the Forget sheet's legacy detection, docs/18 section 3). Counts the key without reading the envelope. */
export async function hasLegacyReserve(): Promise<boolean> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readonly');
  return (await reqPromise<number>(tx.objectStore(KV_STORE).count(KEY_SEALED_RELAYER))) > 0;
}

/**
 * The setup identity the legacy escape hatch captures at its entry and reads again before signing (docs/18 2.4, "The
 * escape hatch's lifetime", steps 2 and 3): the exact `sealedRelayer` and `sealedMaster` records and three fields of
 * `vaultInfo`. A fresh seal draws its own salt and IV, so a replacement setup never equals a captured one.
 */
export interface LegacyIdentity {
  sealedRelayer: SealedRelayer;
  sealedMaster: SealedMaster;
  vaultInfo: { vaultId: string; address: string; relayer: string | undefined };
}

/**
 * One read-only transaction over the three records of `LegacyIdentity`; undefined when any of them is absent. Each
 * value is awaited through its own request's success handler, so a test that holds one read's answer holds this read.
 * It writes nothing, and it leaves the cached connection open, so another tab's Forget or upgrade reaches this tab as
 * `versionchange` for as long as the escape hatch runs (2.4 step 2).
 */
export async function loadLegacyIdentity(): Promise<LegacyIdentity | undefined> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readonly');
  const store = tx.objectStore(KV_STORE);
  const [relayer, master, info] = await Promise.all([reqPromise<unknown>(store.get(KEY_SEALED_RELAYER)), reqPromise<unknown>(store.get(KEY_SEALED_MASTER)), reqPromise<unknown>(store.get(KEY_VAULT_INFO))]);
  if (typeof relayer !== 'object' || relayer === null || typeof master !== 'object' || master === null || typeof info !== 'object' || info === null) return undefined;
  const row = info as Partial<VaultInfo>;
  if (typeof row.vaultId !== 'string' || typeof row.address !== 'string') return undefined;
  // `relayer` is unauthenticated: anything but a string counts as absent (the pre-R48 case), and every opening's key is compared with it anyway.
  return { sealedRelayer: structuredClone(relayer as SealedRelayer), sealedMaster: structuredClone(master as SealedMaster), vaultInfo: { vaultId: row.vaultId, address: row.address, relayer: typeof row.relayer === 'string' ? row.relayer : undefined } };
}

/** Public facts about the vault on this device (address, id, genesis key, bump, the fee reserve's address, a legacy reserve's address): never secret, stored plain so unlock needs no tree derivation. */
export async function loadVaultInfo(): Promise<VaultInfo | undefined> {
  return kvGet<VaultInfo>(KEY_VAULT_INFO);
}

/** What an `updateFeeFields` caller read before it decided to write (docs/18 section 3, writer step 1). */
export interface FeeFieldsExpected {
  /** The exact sealed master record the caller unsealed. */
  sealedMaster: SealedMaster;
  vaultId: string;
  address: string;
  /** The `feeWallet` the caller read, as stored (unauthenticated, so not necessarily a string): undefined for absent, which must still be absent. */
  feeWallet: unknown;
}

/** The only fields the writer may set: the derived n = 0 address, and a legacy reserve's address while none is stored (section 6). */
export interface FeeFieldsPatch {
  feeWallet?: string;
  relayer?: string;
}

/**
 * How the stored `feeWallet` compared with the value the caller read (writer step 3; R92): equal by value, changed, or
 * `incomparable`, a stored value of a kind this build cannot read completely (`compareStoredValue`), which the writer
 * never writes over and never calls equal.
 */
export type FeeFieldComparison = 'same' | 'changed' | 'incomparable';

export interface FeeFieldsResult {
  /** True when the patch was written in this transaction. */
  written: boolean;
  /** The row as stored after the transaction: the written row, or the row found when nothing was written; undefined when no row is stored. */
  current: VaultInfo | undefined;
  /** Whether the stored sealed master equals `expected.sealedMaster` field by field and the row's `vaultId` and `address` equal the expected ones, whatever `feeWallet` holds (writer step 4: another tab of the same setup may have written first). */
  sameSetup: boolean;
  /** The `feeWallet` compare-and-set's answer (`compareStoredValue`); 'changed' when no row is stored at all. */
  comparison: FeeFieldComparison;
}

function sameSealedMaster(a: unknown, b: SealedMaster): boolean {
  if (typeof a !== 'object' || a === null) return false;
  const s = a as Partial<SealedMaster>;
  return s.v === b.v && s.kdf === b.kdf && s.iterations === b.iterations && s.salt === b.salt && s.iv === b.iv && s.ciphertext === b.ciphertext;
}

/**
 * The stored `feeWallet` against the copy a caller read earlier (writer step 3's compare-and-set; docs/18 section 3,
 * R92). The stored value is unauthenticated and need not be a string (tampered, or left by another build), and the
 * caller's copy is a structured clone of the row it loaded, so `===` never matches an object: a device holding one
 * could then never be rewritten. The domain, by the value's structured-clone kind (its `Object.prototype.toString`
 * tag, so a value from another realm compares the same):
 * - primitives, a BigInt included, by `Object.is`; two values of different kinds are 'changed';
 * - by value, member by member: boxed Boolean, Number, String and BigInt; Date (its time); RegExp (source and flags);
 *   ArrayBuffer, every typed array and DataView (their bytes); Array (its length and own enumerable properties, so a
 *   hole is not an undefined); plain Object (own enumerable string properties); Map and Set (each entry of one matched
 *   to a distinct entry of the other equal by value, so an object key or member compares by structure, never by
 *   identity); Error (name, message, stack, cause and own enumerable properties); DOMException (name, message, code).
 *   A pair already under comparison on the current path (a cycle) is equal as far as that path goes, and a difference
 *   anywhere else still answers 'changed';
 * - 'incomparable': any other kind IndexedDB can hand back (a Blob or File, whose bytes need an asynchronous read; an
 *   ImageBitmap; a CryptoKey; a DOM type), where the only honest answer is "not known". A Blob or File whose size,
 *   type, name or date differs is 'changed'. The writer writes nothing over an incomparable value and says so in its
 *   result; the session then adopts the derived key without a write (unlock step 5), so no stored value, however odd,
 *   leaves a device refusing every unlock, and none is ever called equal without being read.
 */
export function compareStoredValue(stored: unknown, expected: unknown): FeeFieldComparison {
  const state = { incomparable: false };
  const equal = sameValue(stored, expected, [], state);
  return !equal ? 'changed' : state.incomparable ? 'incomparable' : 'same';
}

type Pair = [object, object];
type Comparing = { incomparable: boolean };

function sameValue(a: unknown, b: unknown, path: Pair[], state: Comparing): boolean {
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return Object.is(a, b);
  if (a === b) return true;
  const tag = Object.prototype.toString.call(a);
  if (tag !== Object.prototype.toString.call(b)) return false;
  // A pair already under comparison on this path (a cycle): equal as far as this path goes; the rest decides.
  if (path.some(([x, y]) => x === a && y === b)) return true;
  path.push([a, b]);
  try {
    return sameObject(a, b, tag, path, state);
  } finally {
    path.pop();
  }
}

function sameObject(a: object, b: object, tag: string, path: Pair[], state: Comparing): boolean {
  const same = (x: unknown, y: unknown): boolean => sameValue(x, y, path, state);
  switch (tag) {
    case '[object Boolean]':
      return Boolean.prototype.valueOf.call(a) === Boolean.prototype.valueOf.call(b);
    case '[object Number]':
      return Object.is(Number.prototype.valueOf.call(a), Number.prototype.valueOf.call(b));
    case '[object String]':
      return String.prototype.valueOf.call(a) === String.prototype.valueOf.call(b);
    case '[object BigInt]':
      return BigInt.prototype.valueOf.call(a) === BigInt.prototype.valueOf.call(b);
    case '[object Date]':
      return Object.is(Date.prototype.getTime.call(a), Date.prototype.getTime.call(b));
    case '[object RegExp]':
      return (a as RegExp).source === (b as RegExp).source && (a as RegExp).flags === (b as RegExp).flags;
    case '[object ArrayBuffer]':
      return sameBytes(new Uint8Array(a as ArrayBuffer), new Uint8Array(b as ArrayBuffer));
    case '[object Map]':
      return (a as Map<unknown, unknown>).size === (b as Map<unknown, unknown>).size && matched([...(a as Map<unknown, unknown>)], [...(b as Map<unknown, unknown>)], ([k1, v1], [k2, v2]) => same(k1, k2) && same(v1, v2));
    case '[object Set]':
      return (a as Set<unknown>).size === (b as Set<unknown>).size && matched([...(a as Set<unknown>)], [...(b as Set<unknown>)], same);
    case '[object Array]':
      return (a as unknown[]).length === (b as unknown[]).length && sameProperties(a, b, same);
    case '[object Object]':
      return sameProperties(a, b, same);
    case '[object Error]': {
      const x = a as Error & { cause?: unknown };
      const y = b as Error & { cause?: unknown };
      return x.name === y.name && x.message === y.message && x.stack === y.stack && 'cause' in x === 'cause' in y && same(x.cause, y.cause) && sameProperties(a, b, same);
    }
    case '[object DOMException]':
      return (a as DOMException).name === (b as DOMException).name && (a as DOMException).message === (b as DOMException).message && (a as DOMException).code === (b as DOMException).code;
    case '[object Blob]':
    case '[object File]': {
      // Their bytes need an asynchronous read: a different surface is a change; the same surface is "not known".
      const x = a as File;
      const y = b as File;
      if (x.size !== y.size || x.type !== y.type || x.name !== y.name || x.lastModified !== y.lastModified) return false;
      state.incomparable = true;
      return true;
    }
    default:
      if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
        const x = a as ArrayBufferView;
        const y = b as ArrayBufferView;
        return sameBytes(new Uint8Array(x.buffer, x.byteOffset, x.byteLength), new Uint8Array(y.buffer, y.byteOffset, y.byteLength));
      }
      // The same tag on both sides, of a kind this build does not read: not known to be equal, not known to differ.
      state.incomparable = true;
      return true;
  }
}

function sameBytes(x: Uint8Array, y: Uint8Array): boolean {
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/** Own enumerable string properties: the same keys on both sides and each value equal. */
function sameProperties(a: object, b: object, same: (x: unknown, y: unknown) => boolean): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  const o = b as Record<string, unknown>;
  return ka.every((k) => Object.prototype.propertyIsEnumerable.call(o, k) && same((a as Record<string, unknown>)[k], o[k]));
}

/**
 * Every element of `xs` matched to a distinct element of `ys` by `eq`, a multiset equality for Map entries and Set
 * members. `eq` is equality of the comparable parts, an equivalence, so the first fit is as good as any.
 */
function matched<T>(xs: T[], ys: T[], eq: (x: T, y: T) => boolean): boolean {
  const free = ys.slice();
  return xs.every((x) => {
    const i = free.findIndex((y) => eq(x, y));
    if (i < 0) return false;
    free.splice(i, 1);
    return true;
  });
}

/**
 * The compare-and-set writer of the fee fields (docs/18 section 3, R84). One
 * readwrite transaction reads `sealedMaster` and `vaultInfo` and compares
 * them with `expected`: the sealed master record field by field (a fresh
 * seal draws its own salt and IV, so a new setup, even of the same words,
 * never matches an older record), `vaultId`, `address`, and `feeWallet` as a
 * true compare-and-set on the field it overwrites (absent must still be
 * absent, a value must still be that value, compared by value through
 * `compareStoredValue`; a value of a kind it cannot compare is never
 * written over, and `comparison` says so, R92). A `relayer` in the patch is written only while the
 * row has none (an equal one is a no-op); a
 * different stored one refuses the write. When everything matches it writes
 * `{ ...storedRow, ...patch }`: every other field, including fields this
 * build does not know and any `feeGeneration` another build left, comes from
 * the stored row, never from the caller's copy. Otherwise it writes nothing.
 * The caller checks its I3 token immediately before calling (writer step 2)
 * and decides from the result whether to adopt (step 4). A Forget that
 * starts meanwhile either deletes the database after this transaction
 * commits, or leaves a later setup whose sealed master fails the compare
 * (step 5). It deletes nothing.
 */
export async function updateFeeFields(expected: FeeFieldsExpected, patch: FeeFieldsPatch): Promise<FeeFieldsResult> {
  const db = await openAppDb();
  const tx = db.transaction(KV_STORE, 'readwrite', { durability: 'strict' });
  const store = tx.objectStore(KV_STORE);
  const masterReq = store.get(KEY_SEALED_MASTER);
  const infoReq = store.get(KEY_VAULT_INFO);
  let result: FeeFieldsResult = { written: false, current: undefined, sameSetup: false, comparison: 'changed' };
  // Requests in one transaction complete in the order they were made, so the master's read has finished here.
  infoReq.onsuccess = () => {
    const raw: unknown = infoReq.result;
    const row = typeof raw === 'object' && raw !== null ? (raw as VaultInfo) : undefined;
    if (!row) return;
    const sameSetup = sameSealedMaster(masterReq.result, expected.sealedMaster) && row.vaultId === expected.vaultId && row.address === expected.address;
    const comparison = compareStoredValue(row.feeWallet, expected.feeWallet);
    const relayerOk = patch.relayer === undefined || row.relayer === undefined || row.relayer === patch.relayer;
    if (!(sameSetup && comparison === 'same' && relayerOk)) {
      result = { written: false, current: row, sameSetup, comparison };
      return;
    }
    // Only the two fee fields, and only when given: an explicit undefined never erases a stored value.
    const next: VaultInfo = { ...row };
    if (patch.feeWallet !== undefined) next.feeWallet = patch.feeWallet;
    if (patch.relayer !== undefined && row.relayer === undefined) next.relayer = patch.relayer;
    store.put(structuredClone(next), KEY_VAULT_INFO);
    result = { written: true, current: next, sameSetup, comparison };
  };
  await txDone(tx);
  return { ...result, current: result.current === undefined ? undefined : structuredClone(result.current) };
}

export async function saveSettings(s: AppSettings): Promise<void> {
  await kvPut(KEY_SETTINGS, s);
}

export async function loadSettings(): Promise<AppSettings> {
  const s = await kvGet<AppSettings & { development?: boolean }>(KEY_SETTINGS);
  if (!s) return DEFAULT_SETTINGS;
  // The development switch used to be a stored setting; it is session-only now (R42) and any stored value is dropped.
  const { development: _dropped, ...rest } = s;
  return rest;
}

/**
 * The deletion `forgetDevice` last started and that has not finished yet.
 * IndexedDB queues a delete behind every other open connection and offers
 * no way to withdraw it, so once issued it completes when those connections
 * close, whatever this tab shows in the meantime (R60). A retry therefore
 * waits on this same deletion with a fresh timeout rather than queueing a
 * second one (R59). `blocked` resolves once either request has reported
 * that another connection is in its way.
 */
let outstanding: { done: Promise<void>; blocked: Promise<void> } | null = null;

/** The deletion still queued after a blocked forget, to be reconciled when it completes; null when nothing is outstanding. */
export function outstandingForget(): Promise<void> | null {
  return outstanding?.done ?? null;
}

function startDeletion(indexedDB: IDBFactory): { done: Promise<void>; blocked: Promise<void> } {
  let signalBlocked: () => void = () => {};
  const blocked = new Promise<void>((resolve) => {
    signalBlocked = resolve;
  });
  const del = (name: string) =>
    new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error(`failed to delete IndexedDB database ${name}`));
      // The other connections have been told (versionchange); the deletion now waits for them.
      req.onblocked = () => signalBlocked();
    });
  const done = Promise.all([del(APP_DB), del(LEDGER_DB)])
    .then(() => undefined)
    .finally(() => {
      outstanding = null;
    });
  // Whoever awaits through `forgetDevice` or `outstandingForget` sees a failure; this keeps it from also surfacing as unhandled once a timed-out caller has let go.
  done.catch(() => {});
  return { done, blocked };
}

/**
 * Deletes both the app database and the ledger database (5.9 "Forget this
 * device"). Does not touch the vault. The caller locks the session first,
 * so this tab's own ledger connection is closed before the delete (R47);
 * a delete still blocked by another connection `FORGET_BLOCKED_MS` after
 * it reported so rejects with `ForgetBlocked` while the deletion itself
 * keeps waiting. Called again while that deletion is outstanding, it waits
 * on the same deletion with a fresh timeout (R59): the request cannot be
 * withdrawn or issued twice to any effect.
 */
export async function forgetDevice(indexedDB: IDBFactory = globalThis.indexedDB): Promise<void> {
  const dbToClose = dbPromise;
  dbPromise = null;
  if (dbToClose) (await dbToClose).close();

  outstanding ??= startDeletion(indexedDB);
  const deletion = outstanding;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timedOut = deletion.blocked.then(
    () =>
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new ForgetBlocked()), FORGET_BLOCKED_MS);
      }),
  );
  try {
    await Promise.race([deletion.done, timedOut]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
